/**
 * Freelancer availability state.
 *
 * Lightweight client-side store that mirrors what a backend `/availability`
 * endpoint would return. Persists to localStorage so freelancers can set
 * vacation mode immediately even before backend ships, and so other parts
 * of the app (proposal modal, hire modal) can read the same state.
 *
 * Shape:
 *   {
 *     status: 'available' | 'limited' | 'vacation',
 *     daysPerWeek: 1..7,
 *     availableFrom: ISO date string | null,
 *     vacationUntil: ISO date string | null,
 *     hoursPerWeek: number | null,
 *     timezone: string | null,
 *     updatedAt: ISO timestamp,
 *   }
 */

const STORAGE_KEY = 'freelancer_availability';

export const DEFAULT_AVAILABILITY = {
  status: 'available',
  daysPerWeek: 5,
  availableFrom: null,
  vacationUntil: null,
  hoursPerWeek: 40,
  timezone: null,
  updatedAt: null,
};

const safeParse = (raw) => {
  try { return JSON.parse(raw); } catch { return null; }
};

export const loadAvailability = () => {
  if (typeof window === 'undefined') return DEFAULT_AVAILABILITY;
  const raw = window.localStorage?.getItem(STORAGE_KEY);
  return { ...DEFAULT_AVAILABILITY, ...(safeParse(raw) || {}) };
};

export const saveAvailability = (next) => {
  const merged = {
    ...loadAvailability(),
    ...next,
    updatedAt: new Date().toISOString(),
  };
  if (typeof window !== 'undefined') {
    window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(merged));
    window.dispatchEvent(new CustomEvent('availability:changed', { detail: merged }));
  }
  return merged;
};

/**
 * Resolve the *effective* availability of a freelancer right now, taking the
 * vacationUntil and availableFrom dates into account. Returns one of:
 *   - 'available'     - can be invited / messaged immediately
 *   - 'available-soon' - back from vacation or onboarding by `since`
 *   - 'limited'       - working part-time
 *   - 'vacation'      - blocked from invites until `until`
 */
export const resolveAvailability = (availability) => {
  const a = { ...DEFAULT_AVAILABILITY, ...(availability || {}) };
  const now = Date.now();

  if (a.status === 'vacation') {
    if (a.vacationUntil && new Date(a.vacationUntil).getTime() > now) {
      return { state: 'vacation', until: a.vacationUntil };
    }
    // Vacation expired — they are back.
    return { state: 'available' };
  }

  if (a.availableFrom && new Date(a.availableFrom).getTime() > now) {
    return { state: 'available-soon', since: a.availableFrom };
  }

  if (a.status === 'limited' || (a.daysPerWeek && a.daysPerWeek < 5)) {
    return { state: 'limited', daysPerWeek: a.daysPerWeek };
  }

  return { state: 'available' };
};

/**
 * Return true if this freelancer is currently blocked from accepting new
 * invites. Hire / proposal flows call this to gate the action.
 */
export const isInviteBlocked = (availability) => {
  return resolveAvailability(availability).state === 'vacation';
};

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';

export const availabilityLabel = (availability) => {
  const r = resolveAvailability(availability);
  switch (r.state) {
    case 'vacation':
      return `On vacation until ${formatDate(r.until)}`;
    case 'available-soon':
      return `Available from ${formatDate(r.since)}`;
    case 'limited':
      return `${r.daysPerWeek || 4} days/week`;
    default:
      return 'Available now';
  }
};
